import React from 'react';
import {
  isValidTimeSlot,
  isOvernightTime,
  hasTimeOverlap,
} from '../../utils/timeValidation';

// -------------------------
// Types
// -------------------------
interface TimeSlot {
  start: string;
  end: string;
}

interface DaySchedule {
  firstHalf: TimeSlot;
  secondHalf: TimeSlot;
  isClosed: boolean;
}

type OperatingHours = Record<
  'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday',
  DaySchedule
>;

interface DayScheduleListProps {
  operatingHours: OperatingHours;
  onDayChange: (
    day: keyof OperatingHours,
    field: 'firstHalf' | 'secondHalf' | 'isClosed',
    value: any
  ) => void;
  onTimeChange: (
    day: keyof OperatingHours,
    period: 'firstHalf' | 'secondHalf',
    time: 'start' | 'end',
    value: string
  ) => void;
}

const DAYS: Array<keyof OperatingHours> = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
];

// -------------------------
// DayScheduleList Component (View-Only Mode)
// Users can only toggle "Closed" checkbox
// -------------------------
export const DayScheduleList: React.FC<DayScheduleListProps> = ({
  operatingHours,
  onDayChange,
}) => {
  // -------------------------
  // Get next day helper
  // -------------------------
  const getNextDay = (day: keyof OperatingHours): string => {
    const dayIndex = DAYS.indexOf(day);
    const nextDayIndex = (dayIndex + 1) % 7;
    const nextDay = DAYS[nextDayIndex];
    return nextDay.charAt(0).toUpperCase() + nextDay.slice(1, 3);
  };

  // -------------------------
  // Format time to 12-hour format with AM/PM
  // -------------------------
  const formatTime12Hour = (time24: string): string => {
    if (!time24) return '';
    
    const [hours, minutes] = time24.split(':').map(Number);
    const period = hours >= 12 ? 'PM' : 'AM';
    const hours12 = hours % 12 || 12; // Convert 0 to 12 for midnight
    
    return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
  };

  // -------------------------
  // Format time display
  // -------------------------
  const formatTimeDisplay = (data: DaySchedule, currentDay: keyof OperatingHours): string => {
    if (data.isClosed) return 'Closed';

    // ⭐ Check if overnight
    const isOvernight = data.firstHalf.start && data.firstHalf.end && isOvernightTime(data.firstHalf.start, data.firstHalf.end);
    
    // Format first period with next day indicator for overnight
    let firstPeriod: string;
    if (isOvernight) {
      const nextDay = getNextDay(currentDay);
      firstPeriod = `${formatTime12Hour(data.firstHalf.start)} - ${formatTime12Hour(data.firstHalf.end)} (${nextDay}) 🌙`;
    } else {
      firstPeriod = `${formatTime12Hour(data.firstHalf.start)} - ${formatTime12Hour(data.firstHalf.end)}`;
    }

    // Check if second period exists
    if (data.secondHalf.start && data.secondHalf.end) {
      const isSecondOvernight = isOvernightTime(data.secondHalf.start, data.secondHalf.end);
      
      let secondPeriod: string;
      if (isSecondOvernight) {
        const nextDay = getNextDay(currentDay);
        secondPeriod = `${formatTime12Hour(data.secondHalf.start)} - ${formatTime12Hour(data.secondHalf.end)} (${nextDay}) 🌙`;
      } else {
        secondPeriod = `${formatTime12Hour(data.secondHalf.start)} - ${formatTime12Hour(data.secondHalf.end)}`;
      }
      
      return `${firstPeriod}, ${secondPeriod}`;
    }

    return firstPeriod;
  };

  // -------------------------
  // Get validation errors
  // -------------------------
  const getDayValidationErrors = (day: keyof OperatingHours): string[] => {
    const data = operatingHours[day];
    const errors: string[] = [];

    if (data.isClosed) {
      return errors;
    }

    const firstHalf = data.firstHalf;
    const secondHalf = data.secondHalf;

    // First period validation - only check if times are missing
    if (!isValidTimeSlot(firstHalf.start, firstHalf.end)) {
      errors.push('First period: Start or end time is missing or invalid.');
    }
    // Note: Overnight shifts are VALID for first period - no error needed

    const isSecondHalfPopulated = secondHalf.start || secondHalf.end;

    if (isSecondHalfPopulated) {
      // Second period validation
      if (!isValidTimeSlot(secondHalf.start, secondHalf.end)) {
        errors.push('Second period: Start or end time is missing or invalid.');
      } else {
        // Check for overlap between first and second periods
        if (
          isValidTimeSlot(firstHalf.start, firstHalf.end) &&
          isValidTimeSlot(secondHalf.start, secondHalf.end) &&
          hasTimeOverlap(firstHalf.start, firstHalf.end, secondHalf.start, secondHalf.end)
        ) {
          errors.push('First and second periods overlap. Please adjust times.');
        }
      }
    }

    return errors;
  };

  const renderDay = (day: keyof OperatingHours) => {
    const data = operatingHours[day];
    const label = day.charAt(0).toUpperCase() + day.slice(1);
    const dayErrors = getDayValidationErrors(day);
    const isValid = dayErrors.length === 0;

    return (
      <div
        key={day}
        className={`border rounded-lg p-2.5 ${
          isValid
            ? 'border-[var(--border-color)] bg-[var(--bg-secondary)]'
            : 'border-red-300 bg-red-50'
        }`}
      >
        {/* Day Header with Time Display - Read Only */}
        <div>
          <div className="flex items-center gap-2 mb-1">
            <svg className="w-4 h-4 text-[var(--text-secondary)]" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
            </svg>
            <h6 className="text-sm font-semibold text-[var(--text-primary)]">{label}</h6>
          </div>
          {data.isClosed ? (
            <div className="text-sm text-[var(--text-primary)] font-medium">Closed</div>
          ) : (
            <>
              {/* First Period */}
              {data.firstHalf.start && data.firstHalf.end && (
                <div className="text-sm text-[var(--text-primary)] font-medium">
                  <span className="text-xs text-[var(--text-secondary)] mr-2">First Half:</span>
                    {(() => {
                      const isOvernight = isOvernightTime(data.firstHalf.start, data.firstHalf.end);
                      const nextDay = (() => {
                        const dayIndex = DAYS.indexOf(day);
                        const nextDayIndex = (dayIndex + 1) % 7;
                        const nextDay = DAYS[nextDayIndex];
                        return nextDay.charAt(0).toUpperCase() + nextDay.slice(1, 3);
                      })();
                      
                      if (isOvernight) {
                        return `${formatTime12Hour(data.firstHalf.start)} - ${formatTime12Hour(data.firstHalf.end)} (${nextDay}) 🌙`;
                      }
                      return `${formatTime12Hour(data.firstHalf.start)} - ${formatTime12Hour(data.firstHalf.end)}`;
                    })()}
                </div>
              )}
              
              {/* Second Period */}
              {data.secondHalf.start && data.secondHalf.end && (
                <div className="text-sm text-[var(--text-primary)] font-medium">
                  <span className="text-xs text-[var(--text-secondary)] mr-2">Second Half:</span>
                    {(() => {
                      const isSecondOvernight = isOvernightTime(data.secondHalf.start, data.secondHalf.end);
                      const nextDay = (() => {
                        const dayIndex = DAYS.indexOf(day);
                        const nextDayIndex = (dayIndex + 1) % 7;
                        const nextDay = DAYS[nextDayIndex];
                        return nextDay.charAt(0).toUpperCase() + nextDay.slice(1, 3);
                      })();
                      
                      if (isSecondOvernight) {
                        return `${formatTime12Hour(data.secondHalf.start)} - ${formatTime12Hour(data.secondHalf.end)} (${nextDay}) 🌙`;
                      }
                      return `${formatTime12Hour(data.secondHalf.start)} - ${formatTime12Hour(data.secondHalf.end)}`;
                    })()}
                </div>
              )}
            </>
          )}
        </div>

        {/* Validation Messages */}
        {!isValid && (
          <div className="mt-2 space-y-1">
            {dayErrors.map((error, index) => (
              <p key={index} className="text-red-600 text-xs flex items-center space-x-1">
                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
                <span>{error}</span>
              </p>
            ))}
          </div>
        )}
      </div>
    );
  };

  const firstFourDays = DAYS.slice(0, 4); // Mon, Tue, Wed, Thu
  const lastThreeDays = DAYS.slice(4, 7); // Fri, Sat, Sun

  return (
    <div className="mt-4 grid grid-cols-2 gap-4">
      {/* Left Column: 4 days */}
      <div className="space-y-2">
        {firstFourDays.map(renderDay)}
      </div>
      
      {/* Right Column: 3 days */}
      <div className="space-y-2">
        {lastThreeDays.map(renderDay)}
      </div>
    </div>
  );
};
